import dayjs from 'dayjs';
import CalendarConfig from '../../../../consts/CalendarConfig';

/**
 * Regras do valor de uma escala, por tipo (`kind`). Formato gravado
 * (coluna JSON `value` de patient_scale_assessments):
 * - single:   { value: 'III' }
 * - multiple: { values: ['icepick', 'boxcar'] }        (ordem do catálogo)
 * - dual:     { answers: { current: '5', desired: '3' } }
 * - regions:  { regions: { abdomen: { amount: 'few', erythema: 'none' } } }
 * - fields:   { fields: { oiliness: 'O', sensitivity: 'S' } }   (ordem do catálogo)
 * Nada marcado = null (o backend apaga o registro do atendimento).
 */

export const SCALE_KINDS = Object.freeze({
  SINGLE: 'single',
  MULTIPLE: 'multiple',
  DUAL: 'dual',
  REGIONS: 'regions',
  FIELDS: 'fields',
});

const str = (value) => (value == null ? '' : String(value));

export function findOption(scale, value) {
  return (scale.options || []).find((option) => option.value === str(value)) || null;
}

const optionLabel = (scale, value) => {
  const option = findOption(scale, value);
  return option ? option.label : str(value);
};

/** Valores marcados como lista (serve para destacar cards no tipo single e multiple). */
export function selectedValues(scale, value) {
  if (!value) return [];
  if (scale.kind === SCALE_KINDS.MULTIPLE) return Array.isArray(value.values) ? value.values : [];
  if (scale.kind === SCALE_KINDS.SINGLE) return value.value ? [str(value.value)] : [];
  return [];
}

// ─── Alterações (cada clique gera o valor novo, ou null se ficou vazio) ────

/** Escolha única: clicar de novo na opção marcada limpa. */
export function toggleSingle(value, optionValue) {
  return value && value.value === optionValue ? null : { value: optionValue };
}

export function toggleMultiple(scale, value, optionValue) {
  const current = new Set(selectedValues(scale, value));
  if (current.has(optionValue)) current.delete(optionValue);
  else current.add(optionValue);
  const values = scale.options.map((option) => option.value).filter((item) => current.has(item));
  return values.length > 0 ? { values } : null;
}

export function toggleDualAnswer(value, answerKey, optionValue) {
  const answers = { ...((value && value.answers) || {}) };
  if (answers[answerKey] === optionValue) delete answers[answerKey];
  else answers[answerKey] = optionValue;
  return Object.keys(answers).length > 0 ? { answers } : null;
}

export function setRegionCell(value, regionKey, columnKey, cellValue) {
  const regions = { ...((value && value.regions) || {}) };
  const row = { ...(regions[regionKey] || {}) };
  if (cellValue) row[columnKey] = cellValue;
  else delete row[columnKey];
  if (Object.keys(row).length > 0) regions[regionKey] = row;
  else delete regions[regionKey];
  return Object.keys(regions).length > 0 ? { regions } : null;
}

/** Campo do tipo fields: clicar de novo na opção marcada limpa o campo. */
export function toggleFieldValue(scale, value, fieldKey, optionValue) {
  const current = (value && value.fields) || {};
  const next = { ...current };
  if (next[fieldKey] === optionValue) delete next[fieldKey];
  else next[fieldKey] = optionValue;
  const fields = {};
  (scale.fields || []).forEach((field) => { if (next[field.key]) fields[field.key] = next[field.key]; });
  return Object.keys(fields).length > 0 ? { fields } : null;
}

// ─── Leitura ────────────────────────────────────────────────────────────────

/**
 * Diferença entre as duas respostas do tipo dual, em posições da lista de
 * opções: { steps, text } ou null se falta uma das respostas.
 */
export function dualDifference(scale, value) {
  const answers = (value && value.answers) || {};
  const [first, second] = scale.answers || [];
  if (!first || !second) return null;
  const values = scale.options.map((option) => option.value);
  const from = values.indexOf(str(answers[first.key]));
  const to = values.indexOf(str(answers[second.key]));
  if (from < 0 || to < 0) return null;

  const steps = to - from;
  const config = scale.difference || {};
  const [singular = 'opção', plural = 'opções'] = config.unit || [];
  if (steps === 0) return { steps, text: config.equal || 'Sem diferença' };
  const size = Math.abs(steps);
  const direction = steps < 0 ? config.lower : config.higher;
  return { steps, text: [`${size} ${size === 1 ? singular : plural}`, direction].filter(Boolean).join(', ') };
}

const fieldOptionLabel = (field, value) => {
  const option = (field.options || []).find((item) => item.value === str(value));
  return option ? option.label : str(value);
};

/**
 * Resultado calculado do tipo fields (ex.: tipo de Baumann "OSPW"): os valores
 * de todos os campos, na ordem do catálogo, só quando todos estão marcados.
 * Exige `code` na escala.
 */
export function fieldsCode(scale, value) {
  if (!scale || !scale.code || !value || !value.fields) return null;
  const fields = scale.fields || [];
  if (fields.length === 0 || fields.some((field) => !value.fields[field.key])) return null;
  return fields.map((field) => str(value.fields[field.key])).join('');
}

/** Texto curto do valor, para o "última consulta", a aba Saúde e o relatório. */
export function summarizeValue(scale, value) {
  if (!scale || !value) return '';
  switch (scale.kind) {
    case SCALE_KINDS.SINGLE:
      return value.value ? optionLabel(scale, value.value) : '';
    case SCALE_KINDS.MULTIPLE:
      return selectedValues(scale, value).map((item) => optionLabel(scale, item)).join(', ');
    case SCALE_KINDS.DUAL: {
      const answers = value.answers || {};
      const parts = (scale.answers || [])
        .filter((answer) => answers[answer.key])
        .map((answer) => `${answer.label}: ${optionLabel(scale, answers[answer.key])}`);
      const difference = dualDifference(scale, value);
      if (difference) parts.push(`Diferença: ${difference.text}`);
      return parts.join(' · ');
    }
    case SCALE_KINDS.REGIONS: {
      const regions = value.regions || {};
      return (scale.regions || [])
        .filter((region) => regions[region.key])
        .map((region) => `${region.label}: ${summarizeRegionRow(scale, regions[region.key])}`)
        .join(' · ');
    }
    case SCALE_KINDS.FIELDS: {
      const fields = value.fields || {};
      const filled = (scale.fields || []).filter((field) => fields[field.key]);
      const code = fieldsCode(scale, value);
      if (code) return `${code}: ${filled.map((field) => fieldOptionLabel(field, fields[field.key])).join(', ')}`;
      return filled.map((field) => `${field.label}: ${fieldOptionLabel(field, fields[field.key])}`).join(' · ');
    }
    default:
      return '';
  }
}

export function summarizeRegionRow(scale, row) {
  return (scale.columns || [])
    .filter((column) => row && row[column.key])
    .map((column) => {
      const option = column.options.find((item) => item.value === row[column.key]);
      return `${column.label} ${option ? option.label.toLowerCase() : row[column.key]}`;
    })
    .join(', ');
}

/** Cor da opção marcada (tipo single com amostra de cor), para pontos de legenda. */
export function valueColor(scale, value) {
  if (!scale || scale.kind !== SCALE_KINDS.SINGLE || !value) return null;
  const option = findOption(scale, value.value);
  return option && option.color ? option.color : null;
}

/** Valor de exemplo para a prévia na configuração dos tipos de atendimento. */
export function exampleValue(scale) {
  const options = scale.options || [];
  const middle = options[Math.floor(options.length / 2)];
  switch (scale.kind) {
    case SCALE_KINDS.SINGLE:
      return middle ? { value: middle.value } : null;
    case SCALE_KINDS.MULTIPLE:
      return options.length > 0 ? { values: options.slice(0, 2).map((option) => option.value) } : null;
    case SCALE_KINDS.DUAL: {
      const [first, second] = scale.answers || [];
      if (!first || !second || options.length < 2) return null;
      return { answers: { [first.key]: options[options.length - 2].value, [second.key]: options[1].value } };
    }
    case SCALE_KINDS.REGIONS: {
      const region = (scale.regions || [])[0];
      if (!region) return null;
      const row = {};
      (scale.columns || []).forEach((column) => { if (column.options[1]) row[column.key] = column.options[1].value; });
      return { regions: { [region.key]: row } };
    }
    case SCALE_KINDS.FIELDS: {
      const fields = {};
      (scale.fields || []).forEach((field) => { if (field.options[0]) fields[field.key] = field.options[0].value; });
      return Object.keys(fields).length > 0 ? { fields } : null;
    }
    default:
      return null;
  }
}

// ─── Registros (um por atendimento e escala) ───────────────────────────────

export function formatDate(value) {
  return value ? CalendarConfig.GetDDMMAAAA(value) : '';
}

/** Registros desta escala, do mais recente ao mais antigo. */
export function scaleRecords(list, scaleKey) {
  return (Array.isArray(list) ? list : [])
    .filter((record) => record.scaleKey === scaleKey && record.value)
    .sort((a, b) => {
      const byDate = str(b.assessedAt).localeCompare(str(a.assessedAt));
      return byDate !== 0 ? byDate : Number(b.id) - Number(a.id);
    });
}

export function findOwnRecord(records, appointmentId) {
  if (appointmentId == null || appointmentId === '') return null;
  return records.find((record) => str(record.appointmentId) === str(appointmentId)) || null;
}

/**
 * Registro da consulta anterior: o mais recente de outro atendimento, sem
 * passar do dia deste atendimento (abrir uma consulta antiga não mostra o futuro).
 */
export function findPreviousRecord(records, appointmentId, appointmentDate) {
  const limit = appointmentDate && dayjs(appointmentDate).isValid() ? dayjs(appointmentDate).endOf('day') : null;
  return records.find((record) => str(record.appointmentId) !== str(appointmentId)
    && (!limit || !dayjs(record.assessedAt).isAfter(limit))) || null;
}
